import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEOHelmet from '../../components/SEOHelmet';
import { DestinationSchema, BreadcrumbSchema, FAQSchema } from '../../components/SchemaMarkup';
import { countrySeoDdata } from '../../data/seoData';
import { destinations } from '../../data/destinations';
import { placeGuides } from '../../data/placeGuides';
import { routes } from '../../data/routes';
import { FaArrowLeft, FaShare, FaCheck } from 'react-icons/fa';
import './PlaceDetail.css';

const sections = [
  { id: 'overview', label: 'Overview' },
  { id: 'experience', label: 'Our trip' },
  { id: 'visa', label: 'Visa' },
  { id: 'attractions', label: 'What to see' },
  { id: 'route', label: 'Route' },
  { id: 'packing', label: 'Packing' },
  { id: 'tips', label: 'Tips' },
  { id: 'faq', label: 'FAQ' },
];

const PlaceDetail = () => {
  const { country } = useParams();
  const [shareMessage, setShareMessage] = useState('');
  const [activeSection, setActiveSection] = useState('overview');

  // Atlas record and guide record are separate sources; the guide wins on shared keys
  const place = useMemo(() => {
    const base = destinations.find((d) => d.id === country);
    const guide = placeGuides[country];
    if (!base && !guide) return null;
    return { ...base, ...guide };
  }, [country]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [country]);

  useEffect(() => {
    if (!place) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [place]);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${place.name} travel guide`,
          text: place.tagline || place.shortDescription,
          url: window.location.href,
        });
        setShareMessage('Thanks for sharing');
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setShareMessage('Link copied');
      }
      setTimeout(() => setShareMessage(''), 3000);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setShareMessage('Unable to share');
        setTimeout(() => setShareMessage(''), 3000);
      }
    }
  };

  if (!place) {
    return (
      <div className="guide-missing">
        <div className="container">
          <h1>We haven&rsquo;t written this one up yet</h1>
          <p>
            We only publish guides for places we&rsquo;ve actually been, so the atlas is
            deliberately short.
          </p>
          <Link to="/places" className="btn btn-primary">Back to the atlas</Link>
        </div>
      </div>
    );
  }

  const seo = countrySeoDdata[country] || {};
  const route = routes[country];
  const quickFacts = [
    { label: 'Best time', value: place.bestTime },
    { label: 'Currency', value: place.currency },
    { label: 'Language', value: place.language },
    { label: 'Climate', value: place.climate },
    { label: 'Region', value: place.region },
  ].filter((f) => f.value);

  return (
    <div className="guide-page">
      <SEOHelmet
        title={seo.title || `${place.name} Travel Guide | Safarnamah`}
        description={seo.description || `Complete travel guide for ${place.name}: visa requirements, what to pack, best time to visit, top attractions, and practical tips.`}
        keywords={seo.keywords || `${place.name} travel guide, safarnamah`}
        image={seo.image || place.image}
        url={`https://safarnamah.in/places/${country}`}
        type="article"
      />
      <DestinationSchema
        destination={place.name}
        description={seo.longDescription || place.tagline || place.shortDescription}
        image={seo.image || place.image}
        url={`https://safarnamah.in/places/${country}`}
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://safarnamah.in' },
        { name: 'Destinations', url: 'https://safarnamah.in/places' },
        { name: place.name, url: `https://safarnamah.in/places/${country}` },
      ]} />
      {place.faqs && <FAQSchema faqs={place.faqs} />}

      {/* Cinematic opening */}
      <header className="guide-hero">
        <div className="guide-hero-bg" style={{ backgroundImage: `url(${place.image})` }} />
        <div className="guide-hero-scrim" />
        <div className="container guide-hero-in">
          <Link to="/places" className="back-link">
            <FaArrowLeft aria-hidden="true" /> The atlas
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 34 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <span className="eyebrow">{place.region || 'Travel guide'}</span>
            <h1>{place.name}</h1>
            {(place.tagline || place.shortDescription) && (
              <p className="guide-tagline">{place.tagline || place.shortDescription}</p>
            )}
          </motion.div>
        </div>
      </header>

      {/* Facts strip */}
      {quickFacts.length > 0 && (
        <div className="facts-strip">
          <div className="container facts-strip-in">
            {quickFacts.map((f) => (
              <div key={f.label}>
                <dt>{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="guide-body">
        <div className="container guide-grid">
          <article className="guide-main">
            <section id="overview">
              <h2>Overview</h2>
              <div className="prose">
                <p>{place.overview}</p>
              </div>
            </section>

            {place.personalExperience && (
              <section id="experience" className="pull">
                <h2>What it was actually like</h2>
                <div className="prose">
                  <p>{place.personalExperience}</p>
                </div>
              </section>
            )}

            {place.visa && (
              <section id="visa">
                <h2>Visa requirements</h2>
                <div className="prose">
                  <p>{place.visa.summary}</p>
                </div>
                <ul className="ticks">
                  {place.visa.details.map((d) => (
                    <li key={d}><FaCheck aria-hidden="true" /> {d}</li>
                  ))}
                </ul>
                {place.visa.note && <p className="note">{place.visa.note}</p>}
              </section>
            )}

            {place.attractions && (
              <section id="attractions">
                <h2>What to see</h2>
                <div className="attractions">
                  {place.attractions.map((a) => (
                    <article className="attraction" key={a.name}>
                      <h3>{a.name}</h3>
                      <p>{a.description}</p>
                      {a.tip && <p className="tip"><span>Tip</span>{a.tip}</p>}
                    </article>
                  ))}
                </div>
              </section>
            )}

            {route && (
              <section id="route">
                <h2>A route that works</h2>
                <div className="prose">
                  <p>{route.lead}</p>
                </div>
                <ol className="route-preview">
                  {route.stops.map((s) => (
                    <li key={s.name}>
                      <span className="route-preview-name">{s.name}</span>
                      {s.nights > 0 && (
                        <span className="route-preview-nights">
                          {s.nights} {s.nights === 1 ? 'night' : 'nights'}
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
                <Link to={`/routes/${country}`} className="packing-cta">
                  See the full {route.nights}-night route &nbsp;&rarr;
                </Link>
              </section>
            )}

            {place.packing && (
              <section id="packing">
                <h2>What to pack</h2>
                <div className="packing">
                  {Object.entries(place.packing).map(([category, items]) => (
                    <div className="packing-group" key={category}>
                      <h3>{category}</h3>
                      <ul>
                        {items.map((item) => <li key={item}>{item}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
                <Link to={`/checklist/${country}`} className="packing-cta">
                  Open this as a tickable checklist &nbsp;&rarr;
                </Link>
              </section>
            )}

            {place.tips && (
              <section id="tips">
                <h2>Essential tips</h2>
                <ul className="ticks">
                  {(Array.isArray(place.tips) ? place.tips : [place.tips]).map((t) => (
                    <li key={t}><FaCheck aria-hidden="true" /> {t}</li>
                  ))}
                </ul>
              </section>
            )}

            {place.faqs && (
              <section id="faq">
                <h2>Common questions</h2>
                <div className="faq-list">
                  {place.faqs.map(({ question, answer }) => (
                    <details className="faq-item" key={question}>
                      <summary>{question}</summary>
                      <p>{answer}</p>
                    </details>
                  ))}
                </div>
              </section>
            )}
          </article>

          <aside className="guide-aside">
            <nav className="guide-nav" aria-label="Sections">
              <span className="aside-label">On this page</span>
              <ul>
                {sections
                  .filter(({ id }) => {
                    if (id === 'experience') return place.personalExperience;
                    if (id === 'faq') return place.faqs;
                    return true;
                  })
                  .map(({ id, label }) => (
                    <li key={id}>
                      <a href={`#${id}`} className={activeSection === id ? 'on' : ''}>{label}</a>
                    </li>
                  ))}
              </ul>
            </nav>

            <div className="aside-card">
              <h3>Planning this trip?</h3>
              <p>Send us your dates and budget and we&rsquo;ll build the itinerary.</p>
              <Link to="/plan-trip" className="btn btn-primary">Plan my trip</Link>
              <button type="button" className="guide-share" onClick={handleShare}>
                <FaShare aria-hidden="true" /> Share this guide
              </button>
              {shareMessage && <span className="guide-share-msg">{shareMessage}</span>}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default PlaceDetail;
